// The captain's shoal watch: the look-ahead that lights "⚠ SHOAL AHEAD — COME
// AROUND ⚠" while there is still water to turn in, and the clock it sounds
// on. Split out of main.js's frame loop so that clock can be pinned without a
// browser (client/test/shoal.test.mjs).
//
// The watch is meant to re-sound the water ahead twice a second. It shipped
// re-arming 500 ahead on a clock that counts SECONDS (perfNow is
// performance.now() / 1000), so it sounded once, on the captain's first frame
// at the helm, and then not again for eight minutes, through every life that
// followed. A warship steaming for an island met the beach with the banner
// dark, and one that spawned facing a shore wore a stale SHOAL AHEAD long after
// she had come about. So the cadence lives here, in seconds, beside the test
// that sails every class at a real island to keep it there.

// How often the watch re-sounds the water ahead (seconds): twice a second. A
// Fletcher at flank makes ten metres between soundings, a sliver of the look-ahead,
// so the banner never trails the shore by more than that.
export const SHOAL_RECHECK_SEC = 0.5;

// The soundings' spacing along her heading (m). The farthest one sits up to
// this far short of the look-ahead's reach.
const SHOAL_SOUNDING_M = 80;

/** Land inside a warship's look-ahead? Sounds the drawn ground (the field's
 *  surfaceAt: the same triangles the server grounds her on) every
 *  SHOAL_SOUNDING_M along her heading, out to ~15 s of way at flank measured
 *  from her bow (a real-scale Iowa's stem stands 135 m ahead of the point she
 *  turns about), and never less than 240 m past it. (x, z) is the point she
 *  turns about, `yaw` her heading, `type` her class (speedMS, hullHalfLenM). */
export function shoalAhead(field, x, z, yaw, waterLevelM, type) {
  const fx = Math.cos(yaw), fz = -Math.sin(yaw);
  const spd = type?.speedMS ?? 16;
  const bow = type?.hullHalfLenM ?? 0;
  const reach = bow + Math.max(240, spd * 15);
  for (let d = SHOAL_SOUNDING_M; d <= reach; d += SHOAL_SOUNDING_M) {
    if (field.surfaceAt(x + fx * d, z + fz * d) > waterLevelM) return true;
  }
  return false;
}

/** The watch: when the look-ahead runs, and whose water the banner's verdict
 *  was read for. main.js keeps one and drives it every frame the captain has
 *  the helm. */
export class ShoalWatch {
  constructor() {
    this.hullId = null; // the hull the last sounding was taken for (null: off watch)
    this.nextAt = 0; // when the next sounding is due (seconds, the caller's clock)
    this.ahead = false; // the last sounding's verdict: land inside the look-ahead
    this.soundings = 0; // soundings taken; probes read it off window.__va.shoal
  }

  /** One frame at the helm: `now` in SECONDS (perfNow), `sound` the look-ahead.
   *  It runs only when a sounding is due, SHOAL_RECHECK_SEC after the last,
   *  or at once for a hull the last verdict wasn't read for (a fresh spawn, a
   *  swap), so no life inherits another's wait or its water. Returns whether
   *  the banner should light. */
  update(now, hullId, sound) {
    if (hullId !== this.hullId || now >= this.nextAt) {
      this.hullId = hullId;
      this.nextAt = now + SHOAL_RECHECK_SEC;
      this.ahead = !!sound();
      this.soundings++;
    }
    return this.ahead;
  }

  /** Off watch: aground (the grounding banner has the word), off the bridge,
   *  or sunk. The banner goes dark, and the first frame back at the helm
   *  sounds at once instead of trusting water read before she stopped. */
  standDown() {
    this.hullId = null;
    this.ahead = false;
    return false;
  }
}
